import React, { useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { rupiah, num, formatDate, packDisplay } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UnitSelect, rid } from "@/components/UnitSelect";
import { Badge } from "@/components/ui/badge";
import { Plus, CheckCircle2, Pencil, Trash2, Eye, XCircle } from "lucide-react";
import { toast } from "sonner";
import { AuthContext } from "@/App";

const REASONS = ["Barang rusak", "Barang hilang", "Salah kirim", "Salah input", "Retur customer", "Barang tertukar", "Sampel/promosi", "Lainnya"];

const STATUS_MAP = {
  ok: { label: "Sesuai", cls: "bg-emerald-100 text-emerald-800" },
  pending: { label: "Pending Approval", cls: "bg-amber-100 text-amber-800" },
  approved: { label: "Disetujui", cls: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Ditolak", cls: "bg-rose-100 text-rose-800" },
};

function varianceCls(v, bold = false) {
  if (!v) return "";
  if (v < 0) return bold ? "text-rose-700 font-bold" : "text-rose-700";
  return "text-amber-700";
}

// Konversi qty input (satuan dasar/kemasan) → satuan dasar, sama dengan base_qty di backend
const toBase = (p, qty, unit) => {
  const q = Number(qty) || 0;
  if (!p || !unit || unit === p.unit) return q;
  const pk = (p.packs || []).find((x) => x.unit === unit);
  return q * (pk ? Number(pk.factor) || 1 : 1);
};

export default function Opname() {
  const { user } = useContext(AuthContext);
  const canApprove = user?.role === "owner" || user?.role === "manager";
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [salesList, setSalesList] = useState([]);
  const [open, setOpen] = useState(false);
  const [locType, setLocType] = useState("warehouse");
  const [salesId, setSalesId] = useState("");
  const [items, setItems] = useState([]);
  const [sysStock, setSysStock] = useState({});
  const [editingOp, setEditingOp] = useState(null);
  const [opening, setOpening] = useState(false);
  const [detailOp, setDetailOp] = useState(null);
  const [rejectOp, setRejectOp] = useState(null);
  const [rejectNote, setRejectNote] = useState("");

  const load = useCallback(async () => {
    const [a, b, c, s] = await Promise.all([
      api.get("/opnames"), api.get("/products"), api.get("/sales-agents"), api.get("/opnames/stats"),
    ]);
    setRows(a.data); setProducts(b.data); setSalesList(c.data); setStats(s.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Muat stok sistem begitu lokasi dipilih
  useEffect(() => {
    if (!open) return;
    if (locType === "sales" && !salesId) { setSysStock({}); return; }
    const q = locType === "sales" ? `?location_type=sales&sales_id=${salesId}` : "?location_type=warehouse";
    api.get(`/opnames/system-stock${q}`).then((r) => setSysStock(r.data)).catch(() => setSysStock({}));
  }, [open, locType, salesId]);

  useEffect(() => {
    if (!open || editingOp) return;
    // Preload dengan semua produk aktif, fisik = 0 (akan diisi stok sistem begitu termuat)
    setItems(products.map((p) => ({ _k: rid(), product_id: p.product_id, sku: p.sku, name: p.name, physical_qty: 0, unit: p.unit, reason: "", note: "", touched: false })));
  }, [products, open, editingOp]);

  // Default fisik = stok sistem (selisih 0) sampai user mengubahnya sendiri
  useEffect(() => {
    if (!open || editingOp) return;
    setItems((prev) => prev.map((it) => it.touched ? it : { ...it, physical_qty: sysStock[it.product_id] ?? 0 }));
  }, [sysStock, open, editingOp]);

  const openEditOp = (op) => {
    setEditingOp(op);
    setLocType(op.location_type);
    setSalesId(op.sales_id || "");
    setItems(op.items.map((it) => ({ _k: rid(), product_id: it.product_id, sku: it.sku, name: it.name, physical_qty: it.physical_input ?? it.physical_qty, unit: it.input_unit || it.unit, reason: it.reason || "", note: it.note || "" })));
    setOpen(true);
  };

  const setItem = (i, patch) => { const n = [...items]; n[i] = { ...n[i], ...patch }; setItems(n); };
  const prodOf = (it) => products.find((p) => p.product_id === it.product_id);
  const varianceOf = (it) => toBase(prodOf(it), it.physical_qty, it.unit) - (sysStock[it.product_id] ?? 0);
  const totalVarQty = items.reduce((a, it) => a + varianceOf(it), 0);
  const totalVarVal = items.reduce((a, it) => a + varianceOf(it) * (prodOf(it)?.cost_price || 0), 0);

  const deleteOp = async (op) => {
    if (!window.confirm(`Hapus opname ${op.trx_no}?`)) return;
    try { await api.delete(`/opnames/${op.opname_id}`); toast.success("Opname dihapus"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus"); }
  };

  const save = async () => {
    for (const it of items) {
      const v = varianceOf(it);
      if (v !== 0 && !it.reason) { toast.error(`Pilih alasan selisih untuk ${it.sku}`); return; }
      if (v !== 0 && it.reason === "Lainnya" && !String(it.note).trim()) { toast.error(`Isi keterangan untuk ${it.sku} (alasan Lainnya)`); return; }
    }
    try {
      const payload = {
        location_type: locType,
        sales_id: locType === "sales" ? salesId : null,
        items: items.map(({ product_id, physical_qty, unit, reason, note }) => ({ product_id, physical_qty, unit, reason, note })),
        opening,
      };
      if (editingOp) {
        await api.put(`/opnames/${editingOp.opname_id}`, payload);
        toast.success("Opname diperbarui");
      } else {
        await api.post("/opnames", payload);
        toast.success(opening ? "Stok awal langsung disesuaikan" : "Opname tersimpan");
      }
      setOpen(false); setEditingOp(null); setOpening(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const approve = async (op) => {
    if (!window.confirm(`Setujui opname ${op.trx_no}? Stok sistem akan disesuaikan ke jumlah fisik.`)) return;
    try { await api.post(`/opnames/${op.opname_id}/approve`); toast.success("Disetujui — adjustment diposting"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const doReject = async () => {
    try {
      await api.post(`/opnames/${rejectOp.opname_id}/reject`, { note: rejectNote });
      toast.success("Opname ditolak — stok tidak berubah");
      setRejectOp(null); setRejectNote(""); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  return (
    <div data-testid="page-opname" className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Kontrol</div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Stock Opname</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800" data-testid="btn-new-opname" onClick={() => setEditingOp(null)}><Plus className="h-4 w-4 mr-1" />Opname Baru</Button></DialogTrigger>
          <DialogContent className="max-w-6xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingOp ? `Ubah Opname ${editingOp.trx_no}` : "Stock Opname"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Lokasi</Label>
                <Select value={locType} onValueChange={setLocType} disabled={!!editingOp}><SelectTrigger data-testid="input-loc-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="warehouse">Gudang</SelectItem><SelectItem value="sales">Armada Sales</SelectItem></SelectContent>
                </Select>
              </div>
              {locType === "sales" && (
                <div><Label>Sales</Label>
                  <Select value={salesId} onValueChange={setSalesId} disabled={!!editingOp}><SelectTrigger data-testid="input-opname-sales"><SelectValue placeholder="Pilih" /></SelectTrigger>
                    <SelectContent>{salesList.map(s => <SelectItem key={s.sales_id} value={s.sales_id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <table className="w-full text-sm mt-2">
              <thead className="text-xs font-mono uppercase tracking-wider text-slate-500 text-left">
                <tr><th>SKU</th><th>Nama</th><th className="text-right">Stok Sistem</th><th className="text-right">Fisik</th><th>Satuan</th><th className="text-right">Selisih</th><th>Alasan</th></tr>
              </thead>
              <tbody>
                {items.map((it, i) => {
                  const p = prodOf(it);
                  const sys = sysStock[it.product_id] ?? 0;
                  const v = varianceOf(it);
                  return (
                    <tr key={it._k} className="border-b align-top">
                      <td className="py-2 font-mono text-xs">{it.sku}</td>
                      <td>{it.name}</td>
                      <td className="text-right font-mono" data-testid={`sys-${it.sku}`}>
                        {num(sys)} {p?.unit}
                        {packDisplay(sys, p?.unit, p?.packs) && <div className="text-[10px] text-slate-400">{packDisplay(sys, p?.unit, p?.packs)}</div>}
                      </td>
                      <td className="text-right"><Input type="number" value={it.physical_qty} onChange={(e) => setItem(i, { physical_qty: Number(e.target.value), touched: true })} className="w-24 ml-auto" data-testid={`opname-${it.sku}`} /></td>
                      <td className="w-28 pl-2"><UnitSelect product={p} value={it.unit} onChange={(val) => setItem(i, { unit: val })} testid={`opname-unit-${it.sku}`} /></td>
                      <td className={`text-right font-mono pl-2 ${varianceCls(v, true)}`} data-testid={`variance-${it.sku}`}>
                        {v > 0 ? `+${num(v)}` : num(v)}
                        {v !== 0 && packDisplay(v, p?.unit, p?.packs) && <div className="text-[10px]">{packDisplay(v, p?.unit, p?.packs)}</div>}
                      </td>
                      <td className="w-48 pl-2">
                        {v !== 0 ? (
                          <>
                            <Select value={it.reason} onValueChange={(val) => setItem(i, { reason: val })}>
                              <SelectTrigger data-testid={`opname-reason-${it.sku}`}><SelectValue placeholder="Pilih alasan" /></SelectTrigger>
                              <SelectContent>{REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                            </Select>
                            {it.reason === "Lainnya" && <Input className="mt-1" placeholder="Keterangan wajib" value={it.note} onChange={(e) => setItem(i, { note: e.target.value })} data-testid={`opname-note-${it.sku}`} />}
                          </>
                        ) : <span className="text-xs text-slate-400 pl-2">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="flex justify-end gap-6 mt-3 text-sm">
              <div>Total Selisih: <span className={`font-mono font-bold ${varianceCls(totalVarQty, true)}`} data-testid="total-variance-qty">{num(totalVarQty)} unit</span></div>
              <div>Estimasi Nilai: <span className={`font-mono font-bold ${varianceCls(totalVarVal, true)}`} data-testid="total-variance-value">{rupiah(totalVarVal)}</span></div>
            </div>
            {totalVarQty !== 0 && !opening && (
              <p className="text-xs text-amber-700 mt-1" data-testid="pending-hint">Ada selisih → opname berstatus <b>Pending Approval</b>; stok tidak berubah sampai disetujui Owner/Manager. Selisih &gt; 2 kemasan/dus per item atau nilai &gt; Rp500.000 wajib Manager/Owner.</p>
            )}
            {!editingOp && canApprove && (
              <label className="flex items-start gap-2 border border-slate-300 rounded p-3 mt-3 text-sm cursor-pointer hover:bg-slate-50" data-testid="opening-toggle">
                <input type="checkbox" checked={opening} onChange={(e) => setOpening(e.target.checked)} className="mt-0.5 h-4 w-4 accent-slate-900" data-testid="input-opening" />
                <span>
                  <span className="font-medium">Stok Awal / Penyesuaian Langsung</span>
                  <span className="block text-xs text-slate-500 mt-0.5">Stok sistem langsung disesuaikan ke jumlah fisik (adjustment otomatis, tanpa approval). Gunakan untuk penggunaan awal aplikasi agar tidak ada selisih menggantung.</span>
                </span>
              </label>
            )}
            <DialogFooter><Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-save-opname">{opening ? "Simpan & Sesuaikan Stok" : "Simpan"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="opname-stats">
          <StatCard testid="stat-opname-pending" label="Pending Approval" value={num(stats.pending_count)} warn={stats.pending_count > 0} />
          <StatCard testid="stat-opname-today" label="Selisih Hari Ini" value={`${num(stats.today_count)} item`} />
          <StatCard testid="stat-opname-loss" label="Nilai Kerugian Selisih" value={rupiah(stats.loss_value)} warn={stats.loss_value > 0} />
          <StatCard testid="stat-opname-top" label="Paling Sering Selisih" value={stats.top_sales ? `${stats.top_sales.name} (${num(stats.top_sales.count)}×)` : "-"} sub={stats.top_product ? `Barang: ${stats.top_product.name} (${num(stats.top_product.count)}×)` : null} />
        </div>
      )}

      <Card>
        <table className="w-full text-sm" data-testid="table-opnames">
          <thead className="bg-slate-50 text-xs font-mono uppercase tracking-wider text-slate-500 text-left">
            <tr><th className="py-2 px-4">No.</th><th>Tanggal</th><th>Lokasi</th><th className="text-right">Selisih Unit</th><th className="text-right">Nilai Selisih</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const st = STATUS_MAP[r.status] || { label: r.status || "Draft", cls: "bg-slate-100 text-slate-700" };
              return (
                <tr key={r.opname_id} className="border-b">
                  <td className="py-2 px-4 font-mono text-xs">{r.trx_no}</td>
                  <td>{formatDate(r.date)}</td>
                  <td>{r.location_type === "warehouse" ? "Gudang" : `Sales ${r.sales_name}`}</td>
                  <td className={`text-right font-mono ${varianceCls(r.total_variance_qty)}`}>{num(r.total_variance_qty)}</td>
                  <td className={`text-right font-mono ${varianceCls(r.total_variance_value, true)}`}>{rupiah(r.total_variance_value)}</td>
                  <td className="whitespace-nowrap">
                    <Badge className={st.cls} data-testid={`status-${r.trx_no}`}>{st.label}</Badge>
                    {r.status === "pending" && r.needs_manager && <Badge className="ml-1 bg-rose-100 text-rose-800" data-testid={`needsmanager-${r.trx_no}`}>Wajib Manager</Badge>}
                  </td>
                  <td className="whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => setDetailOp(r)} data-testid={`detail-${r.trx_no}`}><Eye className="h-3.5 w-3.5" /></Button>
                    {r.status === "pending" && canApprove && <Button size="sm" onClick={() => approve(r)} data-testid={`approve-${r.trx_no}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Setujui</Button>}
                    {r.status === "pending" && canApprove && <Button variant="ghost" size="sm" onClick={() => { setRejectOp(r); setRejectNote(""); }} data-testid={`reject-${r.trx_no}`}><XCircle className="h-3.5 w-3.5 text-rose-600" /></Button>}
                    {r.status !== "approved" && <Button variant="ghost" size="sm" onClick={() => openEditOp(r)} data-testid={`edit-opname-${r.trx_no}`}><Pencil className="h-3.5 w-3.5" /></Button>}
                    {r.status !== "approved" && <Button variant="ghost" size="sm" onClick={() => deleteOp(r)} data-testid={`delete-opname-${r.trx_no}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-slate-400">Belum ada opname</td></tr>}
          </tbody>
        </table>
      </Card>

      {/* Detail Opname */}
      <Dialog open={!!detailOp} onOpenChange={(o) => !o && setDetailOp(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Detail Opname {detailOp?.trx_no}</DialogTitle></DialogHeader>
          {detailOp && (
            <div data-testid="opname-detail-dialog">
              <div className="text-sm text-slate-600 mb-3 flex gap-4 flex-wrap">
                <span>{formatDate(detailOp.date)}</span>
                <span>{detailOp.location_type === "warehouse" ? "Gudang" : `Sales ${detailOp.sales_name}`}</span>
                <span>Dibuat: {detailOp.user_email}</span>
                {detailOp.approved_by && <span className="text-emerald-700">Disetujui: {detailOp.approved_by} · {detailOp.adj_no}</span>}
                {detailOp.rejected_by && <span className="text-rose-700" data-testid="detail-reject-note">Ditolak: {detailOp.rejected_by}{detailOp.reject_note ? ` — ${detailOp.reject_note}` : ""}</span>}
              </div>
              <table className="w-full text-sm">
                <thead className="text-xs font-mono uppercase tracking-wider text-slate-500 text-left">
                  <tr><th>SKU</th><th>Nama</th><th className="text-right">Sistem</th><th className="text-right">Fisik</th><th className="text-right">Selisih</th><th className="text-right">Nilai</th><th>Alasan</th></tr>
                </thead>
                <tbody>
                  {detailOp.items.map((it) => (
                    <tr key={it.product_id} className="border-b">
                      <td className="py-2 font-mono text-xs">{it.sku}</td>
                      <td>{it.name}</td>
                      <td className="text-right font-mono">{num(it.system_qty)} {it.unit}</td>
                      <td className="text-right font-mono">{num(it.physical_qty)} {it.unit}</td>
                      <td className={`text-right font-mono ${varianceCls(it.variance_qty, true)}`}>{it.variance_qty > 0 ? `+${num(it.variance_qty)}` : num(it.variance_qty)}</td>
                      <td className={`text-right font-mono ${varianceCls(it.variance_value)}`}>{rupiah(it.variance_value)}</td>
                      <td className="pl-2 text-sm">{it.reason || <span className="text-slate-400">—</span>}{it.note ? <div className="text-xs text-slate-500">{it.note}</div> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tolak Opname */}
      <Dialog open={!!rejectOp} onOpenChange={(o) => !o && setRejectOp(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Tolak Opname {rejectOp?.trx_no}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Stok tidak akan berubah. Sales diminta cek ulang perhitungan fisik.</p>
          <div><Label>Catatan Penolakan</Label>
            <Input value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} placeholder="mis. Hitung ulang, stok fisik diragukan" data-testid="reject-note-input" />
          </div>
          <DialogFooter><Button variant="destructive" onClick={doReject} data-testid="btn-confirm-reject">Tolak Opname</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ testid, label, value, sub, warn }) {
  return (
    <div data-testid={testid} className="bg-white rounded-lg border border-slate-200 p-5">
      <div className="text-xs font-mono uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-mono font-bold text-xl mt-1 ${warn ? "text-amber-700" : "text-slate-900"}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}
